import { useIntl, defineMessages } from 'react-intl';
import { useDispatch } from 'react-redux';
import { defaultDataIdFromObject, useMutation } from '@apollo/client';
import * as immutable from 'object-path-immutable';

import CCEvents, { EventNames } from '@companycam/companycam-events';
import { graphQLIdDecoder } from 'helpers/graphql-utils';
import Steven from 'helpers/steven';

import TASK_UPDATE from 'components/projects/taskList/gql/taskUpdate.graphql';
import TASK_CREATE from 'components/projects/taskList/gql/taskCreate.graphql';
import { GET_TASK_LIST_HEADER } from 'components/projects/taskList/gql/getTaskList.graphql';
import {
  GET_TASK_LIST_SECTIONLESS,
  SECTIONLESS_TASKS_PER_PAGE,
} from 'components/projects/taskList/hooks/useGetSectionlessTasks';
import { TaskListSectionsFragment } from 'components/projects/taskList/gql/fragments';
import { getGqlErrors } from 'components/projects/taskList/helpers';
import { setEditingTaskId } from 'actions/todos/taskLists';
import toast from 'helpers/toast';

defineMessages({
  taskUpdateError: {
    id: 'Projects.taskList.task.taskUpdateError',
    defaultMessage: 'Error updating field',
    description: 'message displayed when an error occurs updating a task',
  },
  taskCreateError: {
    id: 'Projects.taskList.task.taskCreateError',
    defaultMessage: 'There was an error creating this field',
    description: 'message displayed when an error occurs creating a task',
  },
});

const NOOP = () => {};

const useTask = ({ taskListId, taskListSectionId, onError = NOOP }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const showErrorToast = (errorMsg) => {
    toast.error(errorMsg);
  };

  const [createTaskMutation, { loading: createTaskLoading }] = useMutation(
    TASK_CREATE,
    {
      update: (cache, { data }) => {
        const gqlErrors = getGqlErrors(data);
        const createdTask = data.taskCreate?.task;

        if (!gqlErrors.length || !createdTask) {
          const cacheId = defaultDataIdFromObject({
            id: taskListId,
            __typename: 'TaskList',
          });

          if (!createdTask.taskListSection) {
            const cachedData = cache.readQuery({
              query: GET_TASK_LIST_SECTIONLESS,
              variables: { id: taskListId, first: SECTIONLESS_TASKS_PER_PAGE },
            });

            const updatedSectionlessTasksData = immutable.push(
              cachedData,
              'viewer.taskList.tasks.nodes',
              createdTask,
            );

            cache.writeQuery({
              query: GET_TASK_LIST_SECTIONLESS,
              variables: {
                id: taskListId,
                first: SECTIONLESS_TASKS_PER_PAGE,
              },
              data: updatedSectionlessTasksData,
            });
          } else {
            const cachedData = cache.readFragment({
              id: cacheId,
              fragment: TaskListSectionsFragment,
              fragmentName: 'TaskListSectionsFragment',
            });
            const sections = cachedData.taskListSections?.nodes || [];
            const sectionIndex = sections
              .map((section) => section.id)
              .indexOf(createdTask.taskListSection.id);

            if (sections.length && sectionIndex >= 0) {
              const updatedSectionTasksData = immutable.push(
                cachedData,
                `taskListSections.nodes.${sectionIndex}.tasks.nodes`,
                createdTask,
              );

              cache.writeFragment({
                id: cacheId,
                fragment: TaskListSectionsFragment,
                fragmentName: 'TaskListSectionsFragment',
                data: updatedSectionTasksData,
              });
            }
          }
        } else {
          showErrorToast(gqlErrors[0].message);
          onError();
        }
      },
      onCompleted: (data) => {
        const gqlErrors = getGqlErrors(data);
        const taskId = data.taskCreate?.task?.id;

        if (!gqlErrors.length) {
          CCEvents.logEvent(EventNames.TASK_LISTS.CREATE_TASK);
          dispatch(setEditingTaskId(taskId));

          const taskDatabaseId = graphQLIdDecoder(taskId)?.databaseId;

          Steven.entityCrud({
            entity_id: taskDatabaseId,
            entity_type: 'checklist_field',
            action: 'created',
          });
        } else {
          showErrorToast(gqlErrors[0].message);
          onError();
        }
      },
      refetchQueries: [
        {
          query: GET_TASK_LIST_HEADER,
          variables: { id: taskListId },
        },
        'GetTaskListTemplateHeaderInfo',
      ],
      onError: () => {
        onError();
        showErrorToast(
          formatMessage({ id: 'Projects.taskList.task.taskCreateError' }),
        );
      },
    },
  );

  const [editTaskMutation] = useMutation(TASK_UPDATE, {
    onCompleted: (data) => {
      const gqlErrors = getGqlErrors(data);

      if (gqlErrors.length) {
        showErrorToast(gqlErrors[0].message);
        onError();
      }
    },
    onError: () => {
      showErrorToast(
        formatMessage({ id: 'Projects.taskList.task.taskUpdateError' }),
      );
      onError();
    },
  });

  const taskCreate = ({
    title = '',
    details = '',
    sectionId,
    instructions,
  }) => {
    createTaskMutation({
      variables: {
        input: {
          taskListId,
          title,
          details,
          instructions,
          photoCaptureRequired: true,
          taskListSectionId: sectionId,
        },
      },
    });
  };

  const taskSave = (task) => {
    if (!task.id) {
      taskCreate({
        title: task.title,
        details: task.details,
        sectionId: taskListSectionId,
        instructions: task.instructions,
      });

      return;
    }

    editTaskMutation({
      variables: {
        input: {
          id: task.id,
          title: task.title,
          details: task.details,
          instructions: task.instructions,
        },
      },
    });
  };

  const createNewTask = ({ sectionId = null } = {}) => {
    taskCreate({ sectionId });
  };

  return {
    taskSave,
    taskCreate,
    createNewTask,
    createTaskLoading,
  };
};

export default useTask;
